import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { Loader2Icon, LogOutIcon } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { authApi } from '@/api/auth'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/keys'
import { signOutEverywhere } from '@/auth/session'
import { useAuthStore } from '@/auth/store'
import { useCurrentUser } from '@/auth/use-auth'
import { FieldError, FormError } from '@/components/field-error'
import { GoogleConnectionCard } from '@/components/integrations/google-connection-card'
import { TimezonePicker } from '@/components/timezone-picker'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { UserAvatar } from '@/components/user-avatar'
import { USER_ROLE_LABELS } from '@/lib/labels'

const MIN_PASSWORD_LENGTH = 10

/** 1.65.0 : Google revient ici, `?google=connected` ou `?google=error&reason=…`. */
interface ProfileSearch {
  google?: 'connected' | 'error'
  reason?: string
}

function readProfileSearch(search: Record<string, unknown>): ProfileSearch {
  return {
    ...(search.google === 'connected' || search.google === 'error'
      ? { google: search.google }
      : {}),
    ...(typeof search.reason === 'string' ? { reason: search.reason } : {}),
  }
}

export const Route = createFileRoute('/_app/settings/profile')({
  validateSearch: readProfileSearch,
  component: ProfilePage,
})

function ProfilePage() {
  const { google, reason } = readProfileSearch(useSearch({ strict: false }))
  const navigate = useNavigate()
  // Le retour de Google se lit une fois : l'adresse est nettoyée, le message reste.
  const [returned] = useState(() => ({ google, reason }))
  useEffect(() => {
    if (!google) return
    if (google === 'connected') toast.success('Compte Google connecté.')
    void navigate({ to: '/settings/profile', search: {}, replace: true })
  }, [google, navigate])

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <IdentityCard />
      <GoogleConnectionCard returned={returned.google} reason={returned.reason} />
      <TimezoneCard />
      <PasswordCard />
      <SessionsCard />
    </div>
  )
}

function IdentityCard() {
  const user = useCurrentUser()
  const setUser = useAuthStore((state) => state.setUser)
  const nameId = useId()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<{ name: string }>({ defaultValues: { name: user.name } })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      const updated = await authApi.updateMe({ name: values.name.trim() })
      setUser(updated)
      reset({ name: updated.name })
      toast.success('Profil mis à jour.')
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Mise à jour impossible.')
    }
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Identité</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormError>{formError}</FormError>

          <div className="flex items-center gap-3">
            <UserAvatar user={user} className="size-11" />
            <div className="min-w-0 text-sm">
              <p className="truncate font-medium">{user.email}</p>
              <p className="text-xs text-muted-foreground">
                {user.role ? USER_ROLE_LABELS[user.role] : 'Contact client'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={nameId}>Nom complet</Label>
            <Input
              id={nameId}
              autoComplete="name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? `${nameId}-error` : undefined}
              {...register('name', {
                required: 'Le nom est obligatoire.',
                minLength: { value: 2, message: 'Nom trop court.' },
              })}
            />
            <FieldError id={`${nameId}-error`}>{errors.name?.message}</FieldError>
          </div>

          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {isSubmitting && <Loader2Icon className="animate-spin" />}
            Enregistrer
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Le fuseau fixe **le jour** de chaque nouvelle saisie : c'est la définition de
 * sa propre journée, donc chacun le règle. Les saisies passées gardent leur
 * jour — une semaine approuvée ne change pas quand on déménage.
 */
function TimezoneCard() {
  const user = useCurrentUser()
  const setUser = useAuthStore((state) => state.setUser)
  const queryClient = useQueryClient()
  const zoneId = useId()
  const current = user.timezone

  const update = useMutation({
    mutationFn: (timezone: string) => authApi.updateMe({ timezone }),
    onSuccess: (updated) => {
      setUser(updated)
      void queryClient.invalidateQueries({ queryKey: queryKeys.timesheet.all })
      toast.success(`Fuseau : ${updated.timezone}.`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Mise à jour impossible.')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fuseau horaire</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor={zoneId}>Votre fuseau</Label>
        <TimezonePicker
          id={zoneId}
          value={current}
          disabled={update.isPending}
          onChange={(zone) => {
            if (zone !== current) update.mutate(zone)
          }}
        />
      </CardContent>
    </Card>
  )
}

interface PasswordForm {
  current_password: string
  new_password: string
  confirmation: string
}

function PasswordCard() {
  const navigate = useNavigate()
  const currentId = useId()
  const newId = useId()
  const confirmationId = useId()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({
    defaultValues: { current_password: '', new_password: '', confirmation: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      const renouvelee = await authApi.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      })
      reset()

      // Deux formes de réponse pendant la bascule du contrat 1.18. Avec un
      // couple de jetons neufs, la session d'où part le geste continue : celui
      // qui vient de prouver qu'il connaît l'ancien mot de passe n'a pas à
      // être mis dehors. Sans corps, le serveur a invalidé ce jeton-ci aussi,
      // et on referme nous-mêmes plutôt que d'attendre un 401 quelques
      // secondes après un message de succès.
      if (renouvelee) {
        useAuthStore.getState().signIn(renouvelee)
        toast.success('Mot de passe modifié. Vos autres sessions ont été fermées.')
        return
      }

      useAuthStore.getState().signOut()
      toast.success('Mot de passe modifié. Reconnectez-vous avec le nouveau.')
      await navigate({ to: '/login', search: { redirect: undefined } })
    } catch (error) {
      if (error instanceof ApiError && (error.status === 400 || error.status === 401)) {
        setFormError('Mot de passe actuel incorrect.')
      } else {
        setFormError(error instanceof ApiError ? error.message : 'Modification impossible.')
      }
    }
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mot de passe</CardTitle>
        {/* Vraie dans les deux cas, sans exposer lequel s'applique : le
            message affiché après le geste dit ce qui s'est réellement passé. */}
        <CardDescription>
          Changer de mot de passe ferme vos sessions sur les autres appareils, et peut vous demander
          de vous reconnecter ici.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormError>{formError}</FormError>

          <div className="space-y-2">
            <Label htmlFor={currentId}>Mot de passe actuel</Label>
            <Input
              id={currentId}
              type="password"
              autoComplete="current-password"
              aria-invalid={!!errors.current_password}
              aria-describedby={errors.current_password ? `${currentId}-error` : undefined}
              {...register('current_password', { required: 'Champ obligatoire.' })}
            />
            <FieldError id={`${currentId}-error`}>{errors.current_password?.message}</FieldError>
          </div>

          <div className="space-y-2">
            <Label htmlFor={newId}>Nouveau mot de passe</Label>
            <Input
              id={newId}
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.new_password}
              aria-describedby={errors.new_password ? `${newId}-error` : `${newId}-hint`}
              {...register('new_password', {
                required: 'Champ obligatoire.',
                minLength: {
                  value: MIN_PASSWORD_LENGTH,
                  message: `Au moins ${MIN_PASSWORD_LENGTH} caractères.`,
                },
              })}
            />
            {errors.new_password ? (
              <FieldError id={`${newId}-error`}>{errors.new_password.message}</FieldError>
            ) : (
              <p id={`${newId}-hint`} className="text-xs text-muted-foreground">
                {MIN_PASSWORD_LENGTH} caractères minimum.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={confirmationId}>Confirmation</Label>
            <Input
              id={confirmationId}
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.confirmation}
              aria-describedby={errors.confirmation ? `${confirmationId}-error` : undefined}
              {...register('confirmation', {
                validate: (value) =>
                  value === watch('new_password') || 'Les deux mots de passe diffèrent.',
              })}
            />
            <FieldError id={`${confirmationId}-error`}>{errors.confirmation?.message}</FieldError>
          </div>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2Icon className="animate-spin" />}
            Changer le mot de passe
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function SessionsCard() {
  const signOutAll = useMutation({
    mutationFn: () => signOutEverywhere(true),
    onSuccess: () => {
      toast.success('Toutes vos sessions ont été fermées.')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sessions</CardTitle>
        <CardDescription>
          Fermer toutes les sessions déconnecte cet appareil ainsi que tous les autres navigateurs
          connectés à votre compte.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          disabled={signOutAll.isPending}
          onClick={() => {
            signOutAll.mutate()
          }}
        >
          {signOutAll.isPending ? <Loader2Icon className="animate-spin" /> : <LogOutIcon />}
          Fermer toutes les sessions
        </Button>
      </CardContent>
    </Card>
  )
}
